<template>
  <footer id="site-footer">
    <section class="footer-top">
      <div class="container">
        <div class="row">
          <div class="col-12 col-md-8">
            <span class="intro">{{ $t('about.description') }}</span>
          </div>

          <div class="col-12 offset-md-2 col-md-2 text-right">
            <img src="@/assets/img/pilihlaptop-logo-min.png"
              alt="PilihLaptop.com"
              title="PilihLaptop.com"
              class="footer-logo"
              loading="lazy">
          </div>
        </div>
      </div>
    </section>

    <section class="footer-middle">
      <div class="container">
        <div class="row">
          <div class="col-12 col-md-2">
            <strong>PilihLaptop.com</strong>
            <template v-for="item in menus.about">
              <router-link
                :to="item.link"
                :key="item.slug"
                class="link-item">
                {{ item.name }}
              </router-link>
            </template>
          </div>

          <div class="col-12 col-md-2">
            <strong>Laptop</strong>
            <template v-for="item in menus.laptops">
              <router-link
                :to="item.link"
                :key="item.slug"
                class="link-item">
                {{ item.name }}
              </router-link>
            </template>
          </div>

          <div class="col-12 col-md-6">
            <strong>{{ $t('general.news') }}</strong>
            <div class="columns">
              <a v-for="item in newsCategories.items"
                :href="item.link"
                :key="item.slug"
                :title="item.name"
                target="_blank"
                rel="noreferrer noopener"
                class="link-item">
                <span v-html="item.name" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="footer-bottom">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-12 col-md-4">
            <span v-html="$t('general.copyright', { year: year, sitename: siteName })" class="copy" />
          </div>

          <div class="col-12 col-md-6">
            <TheUIToggle />
            <span class="link-text" :class="isCurrentLocale('en')" @click="setLocale('en')">EN</span> |
            <span class="link-text" :class="isCurrentLocale('id')" @click="setLocale('id')">ID</span>
            <router-link :to="{ name: 'Terms' }">{{ $t('general.terms_conditions') }}</router-link>
            <router-link :to="{ name: 'Privacy' }">{{ $t('general.privacy_policies') }}</router-link>
          </div>

          <div class="col-12 col-md-2 text-right">
            <div class="social-links">
              <a v-for="(item, index) in social"
                :key="index"
                :href="item.link"
                target="_blank"
                rel="noreferrer noopener"
                :title="`PilihLaptop.com at ${item.name}`"
                class="link-social">
                <i :class="`fab fa-${item.icon}`"></i>
                <span class="sr-only">{{ item.name }} PilihLaptop.com</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </footer>
</template>

<script>
import TheUIToggle from '@/components/common/TheUIToggle.vue';

export default {
  name: 'AppFooter',

  components: {
    TheUIToggle,
  },

  data() {
    return {
      social: [
        {
          name: 'Facebook',
          icon: 'facebook',
          link: 'https://www.facebook.com/PilihLaptopID/',
        },
        {
          name: 'Twitter',
          icon: 'twitter',
          link: 'https://twitter.com/PilihLaptop',
        },
        {
          name: 'Instagram',
          icon: 'instagram',
          link: 'https://instagram.com/pilihlaptop',
        },
      ],
    };
  },

  computed: {
    menus() {
      return {
        about: [
          {
            name: this.$t('general.about_us'),
            slug: 'about',
            link: '/about',
          },
          {
            name: this.$t('general.contact_us'),
            slug: 'contact',
            link: '/contact',
          },
        ],
        laptops: [
          {
            name: this.$t('general.laptop_finder'),
            slug: 'finder',
            link: '/product-finder',
          },
          {
            name: this.$t('general.compare'),
            slug: 'compare',
            link: '/compare',
          },
        ],
      };
    },

    locale() {
      return this.$store.state.locale;
    },

    year() {
      return new Date().getFullYear();
    },

    siteName() {
      return process.env.VUE_APP_SITENAME || 'PilihLaptop.com';
    },

    newsCategories() {
      return this.$store.state.news.categories;
    },
  },

  methods: {
    setLocale(locale) {
      this.$i18n.locale = locale;
      this.$day.locale(locale);
      this.$store.commit('SET_LOCALE', locale);
      document.querySelector('html').setAttribute('lang', locale);
    },

    isCurrentLocale(locale) {
      return (this.locale === locale) ? 'current' : '';
    },
  },
};
</script>

<style lang="scss" scoped>
  #site-footer {
    position: relative;
    padding-top: 5rem;
    padding-bottom: 5rem;
    background-color: $base-color;
    color: $white;
    transition: all 0.3s ease;

    .light & {
      background-color: $white;
      color: $base-color;
      border-top: 1px solid $white-fade;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      display: block;
      height: 8px;
      width: 100%;
      background: $color-primary;
      background: linear-gradient(90deg, rgba($color-primary, 1) 0%, rgba($color-secondary, 1) 100%);
    }

    > section {
      padding: 0;
    }

    a {
      color: $white;

      .light & {
        color: $base-color;
      }
    }

    .footer-top {
      margin-bottom: 3rem;

      .intro {
        opacity: 0.7;
        font-size: 1.125rem;
      }

      .footer-logo {
        width: 6rem;
      }
    }

    .footer-middle {
      margin: 4rem 0;

      .columns {
        columns: 3;
      }

      strong {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
        font-size: 0.75rem;
        color: $white;
        opacity: 0.5;
        transition: all 0.3s ease;

        .light & {
          color: $base-color;
        }
      }

      .link-item {
        display: block;
        line-height: 2;
        color: $white;
        font-size: 0.813rem;
        transition: all 0.3s ease;

        .light & {
          color: $base-color;
        }
      }
    }

    .footer-bottom {
      font-size: 0.75rem;

      a {
        margin-left: 3rem;
      }

      .link-text {
        cursor: pointer;

        &.current {
          font-weight: 700;
        }
      }

      .social-links {
        display: flex;
        align-items: center;
        justify-content: flex-end;

        .link-social {
          margin-left: 1.5rem;
          text-align: center;

          .svg-inline--fa {
            height: 1rem;
            width: 1rem;
          }
        }
      }

      .copy {
        opacity: 0.5;
      }
    }
  }
</style>
